import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, Row, Image } from 'react-bootstrap';
import ReactSelect from 'react-select';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import LoadingButton from 'components/common/LoadingButton';
import useUpload from 'hooks/useUpload';
import { useBeneficiaries } from 'hooks/useBeneficiaries';
import { useSaveCaseStudy } from 'hooks/useCaseStudies';

/**
 * Add / edit a case study: a four-section story (life before Help Yateem, how
 * they found out, their experience, how they are now) about one child, with
 * a date and optional photos. `fixedOrphan` pins the child (beneficiary tab);
 * otherwise the child is picked here (WeConnect page).
 */
const CaseStudyFormModal = ({ show, onHide, caseStudy, fixedOrphan }) => {
  const isEdit = Boolean(caseStudy?.id);
  const { beneficiaries } = useBeneficiaries();
  const { saveCaseStudy, isSaving } = useSaveCaseStudy();
  const { upload, loading: uploading } = useUpload();

  const [orphan, setOrphan] = useState(
    fixedOrphan
      ? {
          value: fixedOrphan.id,
          label: fixedOrphan.name,
          branchId: fixedOrphan.branch_id
        }
      : caseStudy?.orphan
      ? {
          value: caseStudy.orphan.id,
          label: `${caseStudy.orphan.first_name} ${caseStudy.orphan.last_name}`,
          branchId: caseStudy.branch_id
        }
      : null
  );
  const [title, setTitle] = useState(caseStudy?.title || '');
  const [lifeBefore, setLifeBefore] = useState(caseStudy?.life_before || '');
  const [howFoundOut, setHowFoundOut] = useState(
    caseStudy?.how_found_out || ''
  );
  const [experience, setExperience] = useState(caseStudy?.experience || '');
  const [lifeNow, setLifeNow] = useState(caseStudy?.life_now || '');
  const [quote, setQuote] = useState(caseStudy?.quote || '');
  const [studyDate, setStudyDate] = useState(
    caseStudy?.study_date || dayjs().format('YYYY-MM-DD')
  );
  const [photos, setPhotos] = useState(caseStudy?.photos || []);

  const beneficiaryOptions = useMemo(
    () =>
      (beneficiaries || [])
        .filter(b => !b.sponsorship_status?.is_hidden)
        .map(b => ({
          value: b.id,
          label: `${b.first_name} ${b.last_name} (B${b.simple_id})`,
          branchId: b.branch_id
        })),
    [beneficiaries]
  );

  const handleAddPhotos = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        urls.push(await upload(file, 'case-studies'));
      }
      setPhotos(prev => [...prev, ...urls]);
    } catch (err) {
      toast.error('Photo upload failed');
    }
    e.target.value = '';
  };

  const canSave = orphan?.value && title.trim() && studyDate;

  const handleSave = () => {
    saveCaseStudy(
      {
        id: caseStudy?.id,
        orphanId: orphan.value,
        branchId: orphan.branchId,
        title: title.trim(),
        lifeBefore,
        howFoundOut,
        experience,
        lifeNow,
        quote,
        studyDate,
        photos
      },
      { onSuccess: onHide }
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          {isEdit ? 'Edit Case Study' : 'New Case Study'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col md={8}>
            <Form.Label>Beneficiary *</Form.Label>
            <ReactSelect
              value={orphan}
              onChange={setOrphan}
              options={beneficiaryOptions}
              classNamePrefix="react-select"
              placeholder="Select a child..."
              isDisabled={Boolean(fixedOrphan) || isEdit}
            />
          </Col>
          <Col md={4}>
            <Form.Label>Date *</Form.Label>
            <Form.Control
              type="date"
              value={studyDate}
              onChange={e => setStudyDate(e.target.value)}
            />
          </Col>
          <Col md={12}>
            <Form.Label>Title *</Form.Label>
            <Form.Control
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Amina passes her secondary school exams"
            />
          </Col>
          {[
            {
              label: '1. Life before Help Yateem',
              value: lifeBefore,
              set: setLifeBefore,
              placeholder:
                'Their situation before support - family, schooling, health, daily life...'
            },
            {
              label: '2. How they found out about Help Yateem',
              value: howFoundOut,
              set: setHowFoundOut,
              placeholder:
                'Who referred them, how the family or child came into contact with us...'
            },
            {
              label: '3. Their experience with Help Yateem',
              value: experience,
              set: setExperience,
              placeholder:
                'What the sponsorship provided, milestones, what the child or family said...'
            },
            {
              label: '4. How they are now',
              value: lifeNow,
              set: setLifeNow,
              placeholder:
                'Where they are today, what has changed, hopes for the future...'
            }
          ].map(section => (
            <Col md={12} key={section.label}>
              <Form.Label className="fw-medium">{section.label}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={section.value}
                onChange={e => section.set(e.target.value)}
                placeholder={section.placeholder}
              />
            </Col>
          ))}
          <Col md={12}>
            <Form.Label>Quote (optional)</Form.Label>
            <Form.Control
              value={quote}
              onChange={e => setQuote(e.target.value)}
              placeholder="A sentence in the child's or family's own words"
            />
          </Col>
          <Col md={12}>
            <Form.Label>Photos</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              multiple
              onChange={handleAddPhotos}
              disabled={uploading}
            />
            {uploading && <small className="text-600">Uploading…</small>}
            {photos.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {photos.map((url, i) => (
                  <div key={url} className="position-relative">
                    <Image
                      src={url}
                      rounded
                      style={{ width: 96, height: 96, objectFit: 'cover' }}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0 p-0 px-1"
                      style={{ lineHeight: 1 }}
                      title="Remove photo"
                      onClick={() =>
                        setPhotos(prev => prev.filter((_, j) => j !== i))
                      }
                    >
                      <FontAwesomeIcon icon="times" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={onHide}>
          Cancel
        </Button>
        <LoadingButton
          loading={isSaving}
          disabled={!canSave || isSaving || uploading}
          onClick={handleSave}
        >
          {isEdit ? 'Save Changes' : 'Add Case Study'}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
};

CaseStudyFormModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  caseStudy: PropTypes.object,
  fixedOrphan: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    branch_id: PropTypes.string
  })
};

export default CaseStudyFormModal;
